import React, { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import agGridConfig from '../../../agGridConfig';

import columnDefinitions from './columnDefinitions';
import useSelectAgGridRow from '../../../shared/hooks/useSelectAgGridRow.hook';
import { useCreateCurrency, useDeleteCurrency, useUpdateCurrency } from '../../../shared/mutations/currencies';
import { useAdminCurrencies } from '../../../shared/queries/currencies';
import { currentPeriodSelector, environmentSuffixSelector, periodOrCurrentSelector } from '../../../shared/store/selectors';
import alertTypes from '../../../shared/alertTypes';

import { displayZeroSelector, hideDecimalsSelector, negativeMinusSelector } from '../../../userData/store/selectors';

import AdminImportTab from '../shared/adminImportTab.component';
import useAddEditModalControls from '../shared/useAddEditModalControls';
import useRemoveModalControls from '../shared/useRemoveModalControls';
import EditCurrencyCodeAlertContent from './editCurrencyCodeAlertContent.component';

import { Currency, CurrencyFormData, SaveEntityFunction } from '../types';
import AddEditCurrencyModal from './addEditCurrencyModal.component';
import CurrenciesAdminColumnRendererFactory from './currenciesAdminColumnRenderer.component';
import DeleteCurrencyModal from './deleteCurrencyModal.component';
import { useYesNoFlagValues } from '../../../shared/queries/connections';

type CurrenciesTabProps = {
  canModify: boolean,
  hideDecimals: boolean,
  shouldDisplayZero: boolean,
  environmentSuffix: string,
  negativeMinus: boolean,
}

const CurrenciesTab = ({
  canModify,
  hideDecimals,
  shouldDisplayZero,
  environmentSuffix,
  negativeMinus,
}: CurrenciesTabProps) => {
  const {
    selectAgGridRow,
    selectAgGridRowByIndex,
  } = useSelectAgGridRow(agGridConfig.adminClientAdministrationCurrency);

  const {
    hideModalForAddEdit,
    showModalForAdd,
    showModalForEdit,
    entityToEdit,
    isAddEditModalVisible,
  } = useAddEditModalControls<Currency>();

  const {
    hideModalForRemove,
    showModalForRemove,
    entityToRemove,
    rowToRemoveIndex,
    isRemoveModalVisible,
  } = useRemoveModalControls<Currency>();

  const { data: yesNoFlagData, isFetching: isFetchingLookUpData } = useYesNoFlagValues();
  const enabledFlagOptions = useMemo(
    () => yesNoFlagData.map(({ value }) => ({ value, label: value })),
    [yesNoFlagData]
  );

  const {
    isFetching: isFetchingEntities,
    data: entities,
    refetch: fetchEntities,
  } = useAdminCurrencies();


  const { mutate: addEntity, isLoading: isAddingEntity } = useCreateCurrency();
  const { mutate: updateEntity, isLoading: isUpdatingEntity } = useUpdateCurrency();
  const { mutate: removeEntity, isLoading: isRemovingEntity } = useDeleteCurrency(() => {
    hideModalForRemove();
    selectAgGridRowByIndex(rowToRemoveIndex ? rowToRemoveIndex - 1 : 0);
  });

  const onSaveSuccess = useCallback((formData, { keepOpenAfterSaving, callback }) => {
    if (!keepOpenAfterSaving) {
      hideModalForAddEdit();
      selectAgGridRow(formData);
    }

    if (callback) {
      return callback();
    }
  }, [hideModalForAddEdit, selectAgGridRow]);

  const saveEntity: SaveEntityFunction<CurrencyFormData> = useCallback(
    async (formData, openForEdit, options) => {
      if (openForEdit) {
        updateEntity(
          formData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      } else {
        addEntity(
          formData,
          { onSuccess: () => onSaveSuccess(formData, options) }
        );
      }
    }, [addEntity, updateEntity, onSaveSuccess]
  );

  const isSavingEntity = isAddingEntity || isUpdatingEntity;

  const adminColumnRenderer = useMemo(() => CurrenciesAdminColumnRendererFactory({
    showModalForEdit,
    showModalForRemove,
  }), [
    showModalForEdit,
    showModalForRemove,
  ]);

  return (
    <AdminImportTab<
      Currency,
      CurrencyFormData
    >
      pageTitle="Currencies"
      hasPeriodControl={false}
      canModify={canModify}

      AddEditEntityModal={AddEditCurrencyModal}
      RemoveEntityModal={DeleteCurrencyModal}
      adminColumnRenderer={adminColumnRenderer}
      addEntityMessage="Add Currency"
      columnDefs={columnDefinitions}

      isFetchingEntities={isFetchingEntities || isFetchingLookUpData}
      fetchEntities={fetchEntities}
      entities={entities}

      saveEntity={saveEntity}
      isSavingEntity={isSavingEntity}

      removeEntity={removeEntity}
      isRemovingEntity={isRemovingEntity}

      hideDecimals={hideDecimals}
      shouldDisplayZero={shouldDisplayZero}
      environmentSuffix={environmentSuffix}
      negativeMinus={negativeMinus}


      isAddEditModalVisible={isAddEditModalVisible}
      entityToEdit={entityToEdit}

      isRemoveModalVisible={isRemoveModalVisible}
      entityToRemove={entityToRemove}
      rowToRemoveIndex={rowToRemoveIndex}

      showModalForAdd={showModalForAdd}
      hideModalForAddEdit={hideModalForAddEdit}
      hideModalForRemove={hideModalForRemove}

      additionalData={{
        lookUpData: { enabledFlagOptions },
      }}

      alert={{
        content: <EditCurrencyCodeAlertContent />,
        show: true,
        alertType: alertTypes.WARNING,
      }}
    />
  );
};


const mapStateToProps = (state: { shared: any, user: any }) => ({
  period: periodOrCurrentSelector(state),
  currentPeriod: currentPeriodSelector(state),

  environmentSuffix: environmentSuffixSelector(state),
  shouldDisplayZero: displayZeroSelector(state),
  hideDecimals: hideDecimalsSelector(state),
  negativeMinus: negativeMinusSelector(state),
});

export default connect(mapStateToProps)(CurrenciesTab);
